import { FC, Fragment, ReactNode, useEffect } from "react";
import { useLocation } from "react-router";
import { Link } from "react-router-dom";
import { useBreadcrumb } from "./breadcrumb-context";

interface BreadcrumbProps {
  children: ReactNode;
}

interface BreadcrumbItemProps {
  children: ReactNode;
}

const Breadcrumb: FC<BreadcrumbProps> = ({ children }) => {
  return (
    <nav className="flex flex-1" aria-label="Breadcrumb">
      {children}
    </nav>
  );
};

const BreadcrumbList: FC<BreadcrumbProps> = ({ children }) => {
  return <ol className="flex items-center space-x-2">{children}</ol>;
};

const BreadcrumbItem: FC<BreadcrumbItemProps> = ({ children }) => {
  return <li className="inline-flex items-center">{children}</li>;
};

const BreadcrumbLink: FC<{ href: string; children: ReactNode }> = ({ href, children }) => {
  return (
    <Link to={href} className="inline-flex items-center text-sm font-medium text-gray-500 hover:text-gray-700">
      {children}
    </Link>
  );
};

const BreadcrumbSeparator = () => {
  return <span className="mx-2 text-gray-400">/</span>;
};

const BreadcrumbPage: FC<BreadcrumbItemProps> = ({ children }) => {
  return <span className="text-sm font-medium text-gray-900">{children}</span>;
};

const translate = (text: string) => {
  switch (text) {
    case "Contacts":
      return "Kontakte";
    case "Deals":
      return "Deals";
    case "Pipelines":
      return "Pipelines";
    case "Settings":
      return "Einstellungen";
    case "Users":
      return "Benutzer";
    case "Organisations":
      return "Unternehmen";
    case "Persons":
      return "Personen";
    case "Personal":
      return "Account";
    case "Products":
      return "Produkte";
    case "Create":
      return "Erstellen";
    case "Sales-ranks":
      return "Verkaufsränge";
    default:
      return text;
  }
};

const DynamicBreadcrumb = () => {
  const location = useLocation();
  const { title, setTitle, query, setQuery } = useBreadcrumb();

  // Remove trailing slash and split path into segments
  const segments = location.pathname.replace(/\/$/, "").split("/").filter(Boolean);

  useEffect(() => {
    if (setTitle) {
      setTitle(null);
    }
    if (setQuery) {
      setQuery(null);
    }
  }, [location.pathname, setTitle, setQuery]);

  // Handle root route specially
  if (location.pathname === "/" || segments.length === 0) {
    return (
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbPage>{translate(title || "Dashboard")}</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>
    );
  }

  // Generate breadcrumb items
  const items = segments.map((segment, index) => {
    const href = `/${segments.slice(0, index + 1).join("/")}`;
    const label = segment.charAt(0).toUpperCase() + segment.slice(1);
    const isLastItem = index === segments.length - 1;
    const isSecondLastItem = index === segments.length - 2;

    return {
      href,
      label: translate(label),
      isLastItem,
      isSecondLastItem,
    };
  });

  return (
    <Breadcrumb>
      <BreadcrumbList>
        {items.map((item, index) => (
          <Fragment key={item.href}>
            <BreadcrumbItem>
              {item.isLastItem ? (
                <BreadcrumbPage>{title || item.label}</BreadcrumbPage>
              ) : (
                <BreadcrumbLink href={query && item.isSecondLastItem ? `${item.href}?${query}` : item.href}>
                  {item.label}
                </BreadcrumbLink>
              )}
            </BreadcrumbItem>
            {index < items.length - 1 && <BreadcrumbSeparator />}
          </Fragment>
        ))}
      </BreadcrumbList>
    </Breadcrumb>
  );
};

export default DynamicBreadcrumb;
